import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, CrosshairMode } from 'lightweight-charts';

// ── Constants ─────────────────────────────────────────────────────────────────
const BARS_BACK    = 300;  // bars before the center (13F) date
const BARS_FORWARD = 400;  // bars after the center (13F) date

// ── Index definitions — add entries here to support more indexes ──────────────
const INDEXES = [
  { symbol: 'SPY', label: 'S&P 500'      },
  { symbol: 'QQQ', label: 'NASDAQ 100'   },
  { symbol: 'DIA', label: 'Dow Jones'    },
  { symbol: 'IWM', label: 'Russell 2000' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function dateToUnix(isoStr) {
  return Math.floor(new Date(isoStr).getTime() / 1000);
}

function sliceAroundCenter(candles, centerIso, barsBack, barsForward) {
  if (!candles.length) return { sliced: [], centerIdx: 0 };
  const ts = dateToUnix(centerIso);
  let ci = candles.findIndex(c => c.time >= ts);
  if (ci === -1) ci = candles.length - 1;
  const start = Math.max(0, ci - barsBack);
  const end   = Math.min(candles.length, ci + barsForward + 1);
  return { sliced: candles.slice(start, end), centerIdx: ci - start };
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const mkBtn = (active) => ({
  padding: '3px 10px', fontSize: 10, fontWeight: 600,
  border: '1px solid #21262d', borderRadius: 4,
  background: active ? '#58a6ff' : 'transparent',
  color:      active ? '#0d1117' : '#8b949e',
  cursor: 'pointer', fontFamily: 'monospace',
});

const inputStyle = {
  background: '#0d1117', border: '1px solid #30363d', color: '#e6edf3',
  padding: '4px 8px', fontSize: 11, borderRadius: 4,
  fontFamily: 'monospace', outline: 'none', cursor: 'pointer',
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function IndexChart({ centerDate }) {
  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const candleRef    = useRef(null);
  const volRef       = useRef(null);

  const [interval,      setIntervalMode] = useState('1d');
  const [selectedIndex, setSelectedIndex] = useState(INDEXES[0].symbol);  // default SPY
  const [indexDataMap,  setIndexDataMap]  = useState({});   // { SPY: candles[], QQQ: candles[], ... }
  const [loading,       setLoading]       = useState(false);
  const [ohlcv,         setOhlcv]         = useState(null);
  const [dataSource,    setDataSource]    = useState('');

  // ── 1. Init lightweight-charts (once on mount) ──────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      layout: {
        background: { color: '#0d1117' },
        textColor:  '#8b949e',
      },
      grid: {
        vertLines: { color: '#21262d' },
        horzLines: { color: '#21262d' },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: '#21262d' },
      timeScale: {
        borderColor:    '#21262d',
        timeVisible:    true,
        secondsVisible: false,
      },
      handleScroll: true,
      handleScale:  true,
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor:         '#00D4AA',
      downColor:       '#FF6B6B',
      borderUpColor:   '#00D4AA',
      borderDownColor: '#FF6B6B',
      wickUpColor:     '#00D4AA',
      wickDownColor:   '#FF6B6B',
    });

    const volSeries = chart.addHistogramSeries({
      color:        '#26a69a',
      priceFormat:  { type: 'volume' },
      priceScaleId: 'vol',
    });
    chart.priceScale('vol').applyOptions({
      scaleMargins: { top: 0.87, bottom: 0 },
    });

    chart.subscribeCrosshairMove(param => {
      if (!param.time || !param.seriesData?.size) { setOhlcv(null); return; }
      const d = param.seriesData.get(candleSeries);
      if (d) setOhlcv(d);
    });

    chartRef.current  = chart;
    candleRef.current = candleSeries;
    volRef.current    = volSeries;

    const ro = new ResizeObserver(([entry]) => {
      chart.applyOptions({
        width:  entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });
    ro.observe(containerRef.current);

    return () => { ro.disconnect(); chart.remove(); };
  }, []);

  // ── 2. Pre-fetch all indexes when interval changes ─────────────────────────
  // Fetches all indexes in parallel on mount and on Daily/Weekly toggle.
  // Each call is cache-first via the server — subsequent loads return instantly.
  // To add a new index in the future, add it to the INDEXES array above.
  useEffect(() => {
    setLoading(true);
    Promise.all(
      INDEXES.map(idx =>
        fetch(`/api/price/${idx.symbol}?interval=${interval}`)
          .then(r => r.json())
          .then(data => ({ symbol: idx.symbol, candles: data.candles || [], source: data.source || '' }))
          .catch(() => ({ symbol: idx.symbol, candles: [], source: '' }))
      )
    ).then(results => {
      const map = {};
      results.forEach(r => { map[r.symbol] = { candles: r.candles, source: r.source }; });
      setIndexDataMap(map);
      setLoading(false);
    });
  }, [interval]);

  // ── 3. Update data source label when selection or map changes ──────────────
  useEffect(() => {
    setDataSource(indexDataMap[selectedIndex]?.source || '');
  }, [selectedIndex, indexDataMap]);

  // ── 4. Render candles when selected index, data, or centerDate changes ──────
  useEffect(() => {
    if (!candleRef.current || !volRef.current) return;

    const candles = indexDataMap[selectedIndex]?.candles;
    if (!candles?.length) {
      candleRef.current.setData([]);
      volRef.current.setData([]);
      return;
    }

    const center = centerDate
      || new Date((candles[candles.length - 1].time) * 1000).toISOString();

    const { sliced, centerIdx } = sliceAroundCenter(candles, center, BARS_BACK, BARS_FORWARD);
    if (!sliced.length) return;

    candleRef.current.setData(
      sliced.map(c => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close }))
    );

    volRef.current.setData(
      sliced.map(c => ({
        time:  c.time,
        value: c.volume,
        color: c.close >= c.open ? 'rgba(0,212,170,0.3)' : 'rgba(255,107,107,0.3)',
      }))
    );

    // Arrow marker at the 13F quarter date (only if centerDate is provided)
    if (centerDate) {
      const centerCandle = sliced[centerIdx];
      if (centerCandle) {
        candleRef.current.setMarkers([{
          time:     centerCandle.time,
          position: 'aboveBar',
          color:    '#FFEAA7',
          shape:    'arrowDown',
          text:     '13F',
        }]);
      }
    }

    chartRef.current.timeScale().setVisibleLogicalRange({
      from: centerIdx - BARS_BACK,
      to:   centerIdx + BARS_FORWARD,
    });
  }, [selectedIndex, indexDataMap, centerDate]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const resetCenter = useCallback(() => {
    if (!chartRef.current) return;
    const candles = indexDataMap[selectedIndex]?.candles;
    if (!candles?.length || !centerDate) return;
    const { centerIdx } = sliceAroundCenter(candles, centerDate, BARS_BACK, BARS_FORWARD);
    const range = chartRef.current.timeScale().getVisibleLogicalRange();
    const half  = range ? (range.to - range.from) / 2 : 200;
    chartRef.current.timeScale().setVisibleLogicalRange({
      from: centerIdx - half,
      to:   centerIdx + half,
    });
  }, [indexDataMap, selectedIndex, centerDate]);

  const zoom = (factor) => {
    const range = chartRef.current?.timeScale().getVisibleLogicalRange();
    if (!range) return;
    const mid  = (range.from + range.to) / 2;
    const half = ((range.to - range.from) / 2) * factor;
    chartRef.current.timeScale().setVisibleLogicalRange({ from: mid - half, to: mid + half });
  };

  const selectedMeta = INDEXES.find(i => i.symbol === selectedIndex);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 8, padding: 16, marginBottom: 20 }}>

      {/* ── Header row ────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>

        {/* Left: title + selected index name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: '#8b949e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Index Chart
          </span>
          <span style={{ fontSize: 12, color: '#58a6ff', fontWeight: 700 }}>{selectedIndex}</span>
          {selectedMeta && (
            <span style={{ fontSize: 10, color: '#8b949e' }}>{selectedMeta.label}</span>
          )}
          {dataSource && (
            <span style={{ fontSize: 9, color: '#8b949e', opacity: 0.7 }}>[{dataSource}]</span>
          )}
        </div>

        {/* Right: OHLCV readout + controls */}
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
          {ohlcv && (
            <span style={{ fontSize: 10, fontFamily: 'monospace', marginRight: 6, color: '#8b949e' }}>
              O&nbsp;<b style={{ color: '#e6edf3' }}>{ohlcv.open?.toFixed(2)}</b>
              &nbsp;H&nbsp;<b style={{ color: '#00D4AA' }}>{ohlcv.high?.toFixed(2)}</b>
              &nbsp;L&nbsp;<b style={{ color: '#FF6B6B' }}>{ohlcv.low?.toFixed(2)}</b>
              &nbsp;C&nbsp;<b style={{ color: '#58a6ff' }}>{ohlcv.close?.toFixed(2)}</b>
            </span>
          )}
          {/* Index selector dropdown */}
          <select
            value={selectedIndex}
            onChange={e => setSelectedIndex(e.target.value)}
            style={inputStyle}
          >
            {INDEXES.map(idx => (
              <option key={idx.symbol} value={idx.symbol}>
                {idx.symbol} — {idx.label}
              </option>
            ))}
          </select>
          <button style={mkBtn(interval === '1d')}  onClick={() => setIntervalMode('1d')}>Daily</button>
          <button style={mkBtn(interval === '1wk')} onClick={() => setIntervalMode('1wk')}>Weekly</button>
          <button style={mkBtn(false)} onClick={() => zoom(0.5)} title="Zoom in">+</button>
          <button style={mkBtn(false)} onClick={() => zoom(2.0)} title="Zoom out">−</button>
          {centerDate && (
            <button style={mkBtn(false)} onClick={resetCenter} title="Re-center on 13F quarter">⊕</button>
          )}
        </div>
      </div>

      {/* ── Chart area ────────────────────────────────────────────────────── */}
      <div style={{ position: 'relative', height: 380 }}>
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

        {/* Loading overlay */}
        {loading && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            background: 'rgba(13,17,23,0.88)',
          }}>
            <div style={{
              width: 20, height: 20,
              border: '2px solid #21262d', borderTop: '2px solid #58a6ff',
              borderRadius: '50%', animation: 'spin 1s linear infinite',
            }} />
            <span style={{ color: '#8b949e', fontSize: 11 }}>Fetching indexes…</span>
          </div>
        )}
      </div>
    </div>
  );
}
